import { createEnumHelper } from "@/utils/createEnumHelper";

// 供应商文件/供应商物料文件 审核状态
export const FileReviewStatus = {
  Pending: 1, // 待提交
  Submitted: 2, // 已提交
  Approved: 3, // 审核通过
  Rejected: 4, // 审核不通过
  Frozen: 5, // 已冻结
} as const;

type ReviewStatusOption = {
  label: string;
  value: 1 | 2 | 3 | 4 | 5;
  color: string;
};

export const ReviewStatusTags: ReviewStatusOption[] = [
  {
    label: "待上传", // 待提交
    value: FileReviewStatus.Pending,
    color: "#C6C6C6",
  },
  {
    label: "待审核", // 已提交
    value: FileReviewStatus.Submitted,
    color: "#FFB700",
  },
  {
    label: "通过", // 审核通过
    value: FileReviewStatus.Approved,
    color: "#00B42A",
  },
  {
    label: "待重新上传", // 审核不通过
    value: FileReviewStatus.Rejected,
    color: "#FF0000",
  },
  {
    label: "已冻结", // 已冻结
    value: FileReviewStatus.Frozen,
    color: "#C6C6C6",
  },
];

export const ReviewStatusTagsMap = new Map<number, ReviewStatusOption>(
  ReviewStatusTags.map((item) => [item.value, item]),
);

/**
 * 供应商阶段枚举值 1注册阶段, 2评估阶段, 3准入阶段, 4冻结阶段, 5黑名单
 */
export const SUPPLIER_STAGE = {
  /**
   * 注册阶段
   */
  REGISTRATION: 1,
  /**
   * 评估阶段
   */
  EVALUATION: 2,
  /**
   * 准入阶段
   */
  ONBOARDING: 3,
  /**
   * 冻结阶段
   */
  FROZEN: 4,
  /**
   * 黑名单
   */
  BLACKLISTED: 5,
} as const;

export type SupplierStageCode =
  (typeof SUPPLIER_STAGE)[keyof typeof SUPPLIER_STAGE];
export type SupplierStageKey = keyof typeof SUPPLIER_STAGE;

export const SUPPLIER_STAGE_LABELS_ZH: Record<SupplierStageKey, string> = {
  REGISTRATION: "注册阶段",
  EVALUATION: "评估阶段",
  ONBOARDING: "准入阶段",
  FROZEN: "冻结阶段",
  BLACKLISTED: "黑名单",
};

export const { options: SUPPLIER_STAGE_OPTIONS, getText: getSupplerStageText } =
  createEnumHelper(SUPPLIER_STAGE, SUPPLIER_STAGE_LABELS_ZH);

/**
 * 冻结申请类型
 */
export const FREEZE_APPLY_TYPE = {
  /**
   * 冻结申请
   */
  PENDING_SUBMISSION: 0,
  /**
   * 解冻申请
   */
  UNDER_APPROVAL: 1,
  /**
   * 系统冻结
   */
  APPROVED: 2,
  /**
   * 系统解冻
   */
  TERMINATED: 3,
} as const;

export type FreezeApplyTypekey = keyof typeof FREEZE_APPLY_TYPE;
export type FreezeApplyTypeCode =
  (typeof FREEZE_APPLY_TYPE)[FreezeApplyTypekey];

export const FREEZE_APPLY_TYPE_ZH = {
  /**
   * 冻结申请
   */
  PENDING_SUBMISSION: "冻结申请",
  /**
   * 解冻申请
   */
  UNDER_APPROVAL: "解冻申请",
  /**
   * 系统冻结
   */
  APPROVED: "系统冻结",
  /**
   * 系统解冻
   */
  TERMINATED: "系统解冻",
};

export const { options: FREEZE_APPLY_TYPE_OPTIONS } = createEnumHelper(
  FREEZE_APPLY_TYPE,
  FREEZE_APPLY_TYPE_ZH,
);

/**
 * 供应商黑名单申请类型
 */
export const BLACKLIST_APPLY_TYPE = {
  /**
   * 移入申请
   */
  ADD_TO: 0,
  /**
   * 移出申请
   */
  REMOVE_FROM: 1,
} as const;

export type BlacklistApplyTypekey = keyof typeof BLACKLIST_APPLY_TYPE;
export type BlacklistApplyTypeCode =
  (typeof BLACKLIST_APPLY_TYPE)[BlacklistApplyTypekey];

export const BLACKLIST_APPLY_TYPE_ZH = {
  ADD_TO: "移入申请",
  REMOVE_FROM: "移出申请",
};

export const { options: BLACKLIST_APPLY_TYPE_OPTIONS } = createEnumHelper(
  BLACKLIST_APPLY_TYPE,
  BLACKLIST_APPLY_TYPE_ZH,
);

/**
 * 列表申请状态options
 */
export const APPLY_VIEW_STATUS_OPTIONS = [
  {
    label: "审批中",
    value: "审批中",
  },
  {
    label: "已通过",
    value: "已通过",
  },
  {
    label: "已驳回",
    value: "已驳回",
  },
];

export const APPLY_VIEW_STATUS_COLOR_MAP = {
  审批中: "#FFB700",
  已通过: "#00B42A",
  已驳回: "#F53F3F",
};

/** 供应商子帐号状态 */
export const SUPPLIER_SUB_ACCOUNT_STATUS = {
  /**
   * 待注册
   */
  PENDING_REGISTRATION: 1,
  /**
   * 草稿
   */
  DRAFT: 2,
  /**
   * 待审核
   */
  PENDING_REVIEW: 3,
  /**
   * 已通过
   */
  APPROVED: 4,
  /**
   * 驳回
   */
  REJECTED: 5,
  /**
   * 禁用
   */
  DISABLED: 6,
  /**
   * 供应商修改信息后待审核
   */
  PENDING_REVIEW_AFTER_UPDATE: 7,
  /**
   * 供应商修改信息后驳回
   */
  REJECTED_AFTER_UPDATE: 8,
} as const;

export type SupplierSubAccountStatusCode =
  (typeof SUPPLIER_SUB_ACCOUNT_STATUS)[keyof typeof SUPPLIER_SUB_ACCOUNT_STATUS];
export type SupplierSubAccountStatusKey =
  keyof typeof SUPPLIER_SUB_ACCOUNT_STATUS;

export const SUPPLIER_SUB_ACCOUNT_STATUS_ZH: Record<
  SupplierSubAccountStatusKey,
  string
> = {
  PENDING_REGISTRATION: "待注册",
  DRAFT: "草稿",
  PENDING_REVIEW: "待审核",
  APPROVED: "已通过",
  REJECTED: "已驳回",
  DISABLED: "已禁用",
  PENDING_REVIEW_AFTER_UPDATE: "待审核",
  REJECTED_AFTER_UPDATE: "已驳回",
};

export const {
  options: SUPPLIER_SUB_ACCOUNT_STATUS_OPTIONS,
  getText: getSupplierSubAccountStatusText,
} = createEnumHelper(
  SUPPLIER_SUB_ACCOUNT_STATUS,
  SUPPLIER_SUB_ACCOUNT_STATUS_ZH,
);
